import { Drawer } from '@/components';
import { useUpdateFundAdditionalAttributesExecutionDivergences } from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { FundAdditionalAttributesModel } from '../../../../_models/project';
import { useProjectDetailDrawerState } from '../../../_components/ProjectDrawerStateProvider';
import { UpdateDivergenceForm } from './UpdateDivergenceForm';

type Props = {
  hasOneYearsAgo: boolean;
  hasOneYearsAgoProject: boolean;
  hasTwoYearsAgo: boolean;
  hasTwoYearsAgoProject: boolean;
  fiscalYear: number;
  executionDivergences: z.infer<
    typeof FundAdditionalAttributesModel
  >['executionDivergences'];
};

export const UpdateDivergenceFormDrawer: FC<Props> = ({
  hasOneYearsAgo,
  hasOneYearsAgoProject,
  hasTwoYearsAgo,
  hasTwoYearsAgoProject,
  fiscalYear,
  executionDivergences,
}) => {
  const { openMessage } = useMessage();
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const { mutateAsync: updateFundAdditionalAttributesExecutionDivergences } =
    useUpdateFundAdditionalAttributesExecutionDivergences();

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  return (
    <Drawer
      destroyOnClose
      open={drawerState.open && drawerState.type === 'divergence'}
      onClose={handleClose}
    >
      <UpdateDivergenceForm
        hasOneYearsAgo={hasOneYearsAgo}
        hasOneYearsAgoProject={hasOneYearsAgoProject}
        hasTwoYearsAgo={hasTwoYearsAgo}
        hasTwoYearsAgoProject={hasTwoYearsAgoProject}
        fiscalYear={fiscalYear}
        initialValues={{
          executionDivergences: executionDivergences?.length
            ? executionDivergences
            : [
                {
                  reason: null,
                  actualBusinessExpense: null,
                  estimatedBusinessExpense: null,
                },
                {
                  reason: null,
                  actualBusinessExpense: null,
                  estimatedBusinessExpense: null,
                },
              ],
        }}
        onSubmit={async ({ executionDivergences }) => {
          await updateFundAdditionalAttributesExecutionDivergences({
            id: projectId,
            executionDivergences,
          });

          await Promise.all([
            openMessage('success', t('message-update-completed')),
            handleClose(),
          ]);
        }}
      />
    </Drawer>
  );
};
